import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  Loader2,
  Package,
  MapPin,
  Phone,
  User,
  CreditCard,
  Clock,
  Truck,
  Bike,
  ShieldCheck,
  KeyRound,
  FileText,
  AlertTriangle,
  RefreshCcw,
  Download,
  Ruler,
  Box,
  Printer,
  Pencil,
  History,
  IndianRupee,
} from "lucide-react";
import api from "../../services/api";
import PriceCard from "../PriceCard";
import HubPriceCard from "../HubPriceCard";
import StatusBadge from "../StatusBadge";
import { sizeLabels, serviceLabels, vehicleLabels } from "../../utils/orderConstants";
import { downloadInvoice } from "../../utils/invoiceDownload";
import ShippingLabelDialog from "./ShippingLabelDialog";
import OrderEditDialog from "./OrderEditDialog";
import PayDifferenceDialog from "./PayDifferenceDialog";
import PayNowDialog from "./PayNowDialog";
import RefundTimelineCard from "./RefundTimelineCard";
import HubRouteCard from "./HubRouteCard";
import ProofGallery from "../ProofGallery";
import NPSPrompt from "../NPSPrompt";

/**
 * OrderDetailsDialog — shows the full order detail view for admins, agents,
 * and order owners. Backend (routes/orders.py::get_order) already applies
 * Sprint 49 role-gating, so we just render whatever fields come back.
 *
 * Contract:
 *   - open / onOpenChange: controlled dialog state
 *   - trackingId: string (re-fetches on change)
 *   - role: "admin" | "agent" | "user" (passed from parent so we can hide
 *     admin-only sections even if the payload includes them by accident)
 *
 * Why a fresh fetch per open: the list-endpoint payload is intentionally
 * compact (no admin_notes / cancellation / damage info); the detail
 * endpoint returns the fat object with PII + admin-only fields properly
 * scrubbed per-role.
 */
export default function OrderDetailsDialog({ open, onOpenChange, trackingId, role }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [order, setOrder] = useState(null);
  const [labelOpen, setLabelOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payNowOpen, setPayNowOpen] = useState(false);

  useEffect(() => {
    if (!open || !trackingId) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setOrder(null);
    setHistory([]);
    setHistoryOpen(false);
    (async () => {
      try {
        const { data } = await api.get(`/orders/${trackingId}`);
        if (!cancelled) setOrder(data);
        // Load audit history in background — visible to admin + owner.
        try {
          const h = await api.get(`/orders/${trackingId}/history`);
          if (!cancelled) setHistory(h?.data?.history || []);
        } catch {
          // 403 for non-admin non-owner is expected; ignore.
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load order");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, trackingId]);

  const refreshOrder = async () => {
    try {
      const { data } = await api.get(`/orders/${trackingId}`);
      setOrder(data);
      const h = await api.get(`/orders/${trackingId}/history`);
      setHistory(h?.data?.history || []);
    } catch {
      // ignore — outer error already showed up
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="order-details-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-xl font-bold flex items-center gap-2">
            <Package className="h-5 w-5 text-[#FF6B00]" />
            Order Details
            {order?.tracking_id && (
              <span className="font-mono text-sm text-slate-500" data-testid="order-details-tracking-id">
                · {order.tracking_id}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Full operational view for this shipment.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-16" data-testid="order-details-loading">
            <Loader2 className="h-6 w-6 animate-spin text-[#FF6B00]" />
            <span className="ml-3 text-sm text-slate-500">Loading order…</span>
          </div>
        )}

        {err && !loading && (
          <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="order-details-error">
            {err}
          </div>
        )}

        {order && !loading && !err && (
          <OrderDetailsBody
            order={order}
            role={role}
            onPay={() => setPayOpen(true)}
            onPayNow={() => setPayNowOpen(true)}
          />
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 mt-2 pt-4 border-t border-slate-100">
          {/* Edit Order — visible to admin always; agent if assigned; owner pre-pickup */}
          {order && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditOpen(true)}
              className="rounded-xl"
              data-testid="order-details-edit-btn"
            >
              <Pencil className="h-4 w-4 mr-1.5" /> Edit
            </Button>
          )}
          {/* Audit history — admin + owner only (backend gates response) */}
          {history && history.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHistoryOpen((v) => !v)}
              className="rounded-xl"
              data-testid="order-details-history-toggle-btn"
            >
              <History className="h-4 w-4 mr-1.5" /> {historyOpen ? "Hide" : "Show"} Edit History ({history.length})
            </Button>
          )}
          {/* Print Shipping Label — admin always, agent only after accepting */}
          {order?.tracking_id && (role === "admin" || (role === "agent" && order.agent_id)) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLabelOpen(true)}
              className="rounded-xl border-orange-200 text-[#FF6B00] hover:bg-orange-50"
              data-testid="order-details-print-label-btn"
            >
              <Printer className="h-4 w-4 mr-1.5" /> Print Shipping Label
            </Button>
          )}
          {order?.tracking_id && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadInvoice(order.tracking_id)}
              className="rounded-xl"
              data-testid="order-details-invoice-btn"
            >
              <Download className="h-4 w-4 mr-1.5" /> Invoice
            </Button>
          )}
          <Button
            onClick={() => onOpenChange(false)}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
            data-testid="order-details-close-btn"
          >
            Close
          </Button>
        </div>

        {/* Audit history panel — slides open below the action bar */}
        {historyOpen && history.length > 0 && (
          <Section icon={History} title="Edit History" testId="order-details-history">
            <ol className="space-y-3">
              {history.map((row, i) => (
                <li key={row._id || i} className="border-l-2 border-slate-200 pl-3" data-testid={`history-row-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{row.edited_by_role}</span>
                    <span className="text-sm font-semibold text-slate-800">{row.edited_by_name}</span>
                    <span className="text-xs text-slate-400">· {row.edited_at?.replace("T", " ").slice(0, 19)}</span>
                  </div>
                  {row.reason && (
                    <p className="text-xs italic text-slate-600 mt-0.5">"{row.reason}"</p>
                  )}
                  {row.diff?.length ? (
                    <ul className="mt-1 space-y-0.5">
                      {row.diff.map((d, j) => (
                        <li key={`${row.audit_id || row.id || i}-${d.field || j}`} className="text-xs font-mono text-slate-700" data-testid={`history-row-${i}-diff-${j}`}>
                          <span className="font-bold">{d.field}</span>:{" "}
                          <span className="line-through text-slate-400">{String(d.old_value ?? "—")}</span>{" "}→{" "}
                          <span className="text-slate-900 font-semibold">{String(d.new_value ?? "—")}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ol>
          </Section>
        )}
      </DialogContent>

      {/* 4×6 Shipping Label */}
      <ShippingLabelDialog
        open={labelOpen}
        onOpenChange={setLabelOpen}
        order={order}
      />

      {/* Sprint 53 — Edit Order dialog */}
      <OrderEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        order={order}
        role={role}
        onSaved={async (newOrder) => {
          // null `newOrder` ⇒ submitted as edit-request, not direct edit.
          await refreshOrder();
        }}
      />

      {/* Sprint 80 — Pay difference dialog */}
      <PayDifferenceDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        trackingId={order?.tracking_id}
        onPaid={async () => { await refreshOrder(); }}
      />

      {/* Phase 2 P1 — Post-order Pay Now / COD→Prepaid dialog */}
      <PayNowDialog
        open={payNowOpen}
        onOpenChange={setPayNowOpen}
        trackingId={order?.tracking_id}
        onPaid={async () => { await refreshOrder(); }}
      />
    </Dialog>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  Presentational sub-components                                           */
/* ──────────────────────────────────────────────────────────────────────── */

function Section({ icon: Icon, title, testId, children, danger }) {
  return (
    <div
      className={`rounded-xl p-4 border ${danger ? "bg-red-50 border-red-200" : "bg-white border-slate-100"}`}
      data-testid={testId}
    >
      <div className={`flex items-center gap-2 mb-3 text-xs font-bold uppercase tracking-widest ${danger ? "text-red-700" : "text-slate-500"}`}>
        <Icon className="h-3.5 w-3.5" />
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, testId, mono }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex flex-col gap-0.5" data-testid={testId}>
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <span className={`text-sm text-slate-800 ${mono ? "font-mono" : "font-medium"}`}>{value}</span>
    </div>
  );
}

function formatDT(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function OrderDetailsBody({ order, role, onPay, onPayNow }) {
  const isAdmin = role === "admin";
  const isAgent = role === "agent";
  const isOwner = role === "user" || role === "customer" || (!isAdmin && !isAgent);

  // Phase 2 P1 — Pay Now / COD→Prepaid eligibility (mirrors backend
  // PICKUP_REACHED_STATUSES). Backend re-validates on every endpoint
  // hit, so this is purely a UX gate to hide the button when it would
  // 409 anyway.
  const PICKUP_REACHED = new Set([
    "picked_up", "at_origin_hub", "in_linehaul", "at_dest_hub",
    "in_transit", "out_for_delivery", "delivered", "cancelled", "returned",
  ]);
  const paymentStatusLower = (order.payment_status || "").toLowerCase();
  const orderStatusLower = (order.status || "").toLowerCase();
  const orderTotal = Number(
    (order.price && (order.price.final_price ?? order.price.total)) || 0
  );
  const payNowEligible = (
    isOwner
    && paymentStatusLower !== "paid"
    && !PICKUP_REACHED.has(orderStatusLower)
    && orderTotal > 0
    && typeof onPayNow === "function"
  );
  const payNowLabel = ((order.payment_method || "").toLowerCase() === "cod")
    ? "Pay now & switch to prepaid"
    : "Pay now";

  // Status timeline from individual timestamps — backend doesn't persist a
  // status_history array on orders, so we synthesise one from the events
  // that ARE persisted. Only shows events with a real timestamp.
  const timeline = [
    { label: "Order placed", at: order.created_at, icon: Package },
    { label: `Accepted by ${order.accepted_by_name || "agent"}`, at: order.accepted_at, icon: ShieldCheck },
    { label: "Delivered", at: order.delivered_at, icon: Truck },
    { label: "Cancelled", at: order.cancelled_at, icon: AlertTriangle, danger: true },
  ].filter(e => e.at);

  const dims = order.ai_dimensions || {};
  const hasManualDims = order.length_cm || order.width_cm || order.height_cm;
  const hasPII = order.user_phone || order.pickup_phone || order.delivery_phone;
  const isHub = order.delivery_mode === "hub_routed";

  return (
    <div className="space-y-4">
      {/* ── Header strip: status + payment + speed ── */}
      <div className="flex flex-wrap items-center gap-2 pb-2">
        <StatusBadge status={order.status} />
        {order.payment_method && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200" data-testid="order-details-payment-chip">
            <CreditCard className="h-3 w-3" />
            {order.payment_method.toUpperCase()} · {order.payment_status || "—"}
          </span>
        )}        {payNowEligible && (
          <Button
            onClick={onPayNow}
            size="sm"
            className="h-7 px-2.5 rounded-lg bg-[#FF6B00] hover:bg-[#e55f00] text-white text-xs font-semibold shadow-sm shadow-[#FF6B00]/20"
            data-testid="order-details-pay-now-btn"
          >
            <IndianRupee className="h-3.5 w-3.5 mr-0.5" />
            {payNowLabel}
          </Button>
        )}
        {order.delivery_speed && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-orange-50 text-[#FF6B00] border border-orange-200">
            {order.delivery_speed === "express" ? "Express" : order.delivery_speed === "standard" ? "Standard" : order.delivery_speed}
          </span>
        )}
        {order.vehicle_type && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
            {order.vehicle_type === "bike" ? <Bike className="h-3 w-3" /> : <Truck className="h-3 w-3" />}
            {vehicleLabels[order.vehicle_type] || order.vehicle_type}
          </span>
        )}
        {order.service_type && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
            {serviceLabels[order.service_type] || "Delivery"}
          </span>
        )}
      </div>

      {/* ── Customer ── */}
      {(order.user_name || order.user_email || hasPII) && (
        <Section icon={User} title="Customer" testId="order-details-customer">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Name" value={order.user_name} testId="od-customer-name" />
            <Field label="Email" value={order.user_email} testId="od-customer-email" />
            <Field label="Phone" value={order.user_phone} testId="od-customer-phone" />
          </div>
        </Section>
      )}

      {/* ── Pickup leg ── */}
      <Section icon={MapPin} title="Pickup Address" testId="order-details-pickup">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(order.pickup_house_no || order.pickup_street || order.pickup_flat_no) ? (
            <>
              <Field label="House No" value={order.pickup_house_no || "N/A"} testId="od-pickup-house-no" />
              <Field label="Street" value={order.pickup_street || "N/A"} testId="od-pickup-street" />
              <Field label="Flat / Apartment No" value={order.pickup_flat_no || "N/A"} testId="od-pickup-flat-no" />
            </>
          ) : null}
          <div className="sm:col-span-2">
            <Field label="Full Address" value={order.pickup_address} testId="od-pickup-address" />
          </div>
          {order.pickup_contact_name && (
            <Field label="Sender" value={order.pickup_contact_name} testId="od-pickup-contact-name" />
          )}
          {order.pickup_phone && (
            <Field label="Sender Phone" value={order.pickup_phone} testId="od-pickup-phone" />
          )}
          {order.pickup_type === "scheduled" && (
            <Field
              label="Scheduled"
              value={`${order.scheduled_date || ""} ${order.scheduled_time || ""}`.trim()}
              testId="od-pickup-scheduled"
            />
          )}
        </div>
      </Section>

      {/* ── Drop leg ── */}
      <Section icon={MapPin} title="Drop Address" testId="order-details-drop">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(order.delivery_house_no || order.delivery_street || order.delivery_flat_no) ? (
            <>
              <Field label="House No" value={order.delivery_house_no || "N/A"} testId="od-drop-house-no" />
              <Field label="Street" value={order.delivery_street || "N/A"} testId="od-drop-street" />
              <Field label="Flat / Apartment No" value={order.delivery_flat_no || "N/A"} testId="od-drop-flat-no" />
            </>
          ) : null}
          <div className="sm:col-span-2">
            <Field label="Full Address" value={order.delivery_address} testId="od-drop-address" />
          </div>
          {order.delivery_contact_name && (
            <Field label="Receiver" value={order.delivery_contact_name} testId="od-drop-contact-name" />
          )}
          {order.delivery_phone && (
            <Field label="Receiver Phone" value={order.delivery_phone} testId="od-drop-phone" />
          )}
          {order.delivery_pincode && (
            <Field label="Pincode" value={order.delivery_pincode} testId="od-drop-pincode" mono />
          )}
        </div>
      </Section>

      {/* Multi-city scalability — hub route chain for hub-routed orders */}
      {isHub && order.tracking_id && (
        <HubRouteCard trackingId={order.tracking_id} />
      )}

      {/* ── Package details ── */}
      <Section icon={Box} title="Package" testId="order-details-package">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field
            label="Description"
            value={order.package_description}
            testId="od-package-desc"
          />
          {order.item_size && (
            <Field label="Size" value={sizeLabels[order.item_size] || order.item_size} testId="od-package-size" />
          )}
          {order.package_weight != null && (
            <Field label="Weight" value={`${order.package_weight} kg`} testId="od-package-weight" />
          )}
          {order.chargeable_weight_kg != null && order.chargeable_weight_kg !== order.package_weight && (
            <Field
              label="Chargeable"
              value={`${order.chargeable_weight_kg} kg`}
              testId="od-package-chargeable"
            />
          )}
          {hasManualDims && (
            <Field
              label="L × W × H"
              value={`${order.length_cm || "?"} × ${order.width_cm || "?"} × ${order.height_cm || "?"} cm`}
              testId="od-package-dims"
            />
          )}
          {order.volume_cm3 && (
            <Field
              label="Volume"
              value={`${Number(order.volume_cm3).toLocaleString()} cm³`}
              testId="od-package-volume"
            />
          )}
        </div>
        {dims && (dims.item_detected || dims.packing_tips) && (
          <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5" data-testid="od-ai-dims">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
              <Ruler className="h-3 w-3" /> AI Dimension Scan
            </div>
            {dims.item_detected && <p className="text-xs text-slate-600">{dims.item_detected}</p>}
            {dims.packing_tips && <p className="text-xs text-[#FF6B00] font-semibold">Tip: {dims.packing_tips}</p>}
          </div>
        )}
        {order.notes && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <Field label="Customer Notes" value={order.notes} testId="od-notes" />
          </div>
        )}
      </Section>

      {/* ── Price breakdown ── */}
      {order.price && (
        <Section icon={CreditCard} title="Price Breakdown" testId="order-details-price">
          {(() => {
            // Sprint 55 — pending settlement / refund banner. Sums unsettled
            // amounts and renders a tinted alert above the price card so
            // every role sees the open balance without expanding the order.
            const pendingAdd = (order.pending_settlements || [])
              .filter((p) => p?.status === "pending")
              .reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
            const pendingRefund = (order.pending_refunds || [])
              .filter((p) => p?.status === "pending")
              .reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
            if (!pendingAdd && !pendingRefund) return null;
            return (
              <div
                className="mb-3 rounded-lg border p-3"
                style={{
                  background: pendingAdd > 0 ? "rgba(254, 243, 199, 0.5)" : "rgba(219, 234, 254, 0.5)",
                  borderColor: pendingAdd > 0 ? "#fcd34d" : "#bfdbfe",
                }}
                data-testid="order-details-pending-money"
              >
                {pendingAdd > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-amber-800">Additional amount pending</span>
                    <span className="font-extrabold text-amber-800" data-testid="order-details-pending-amount">₹{pendingAdd}</span>
                  </div>
                )}
                {pendingRefund > 0 && (
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="font-semibold text-blue-800">Refund / adjustment pending</span>
                    <span className="font-extrabold text-blue-800">₹{pendingRefund}</span>
                  </div>
                )}
                <p className="mt-1 text-[10px] text-slate-500">
                  Triggered by an address change. Admin processes settlement / refund manually.
                </p>
                {pendingAdd > 0 && isOwner && onPay && (
                  <Button
                    onClick={onPay}
                    className="mt-3 w-full bg-amber-600 text-white hover:bg-amber-700 rounded-xl"
                    size="sm"
                    data-testid="order-details-pay-difference-btn"
                  >
                    <CreditCard className="h-4 w-4 mr-1.5" /> Pay ₹{pendingAdd} difference
                  </Button>
                )}
              </div>
            );
          })()}

          {/* Sprint 81 — Refund timeline (cancellation + edit-reprice) */}
          {order?.tracking_id && (
            <div className="my-3">
              <RefundTimelineCard trackingId={order.tracking_id} />
            </div>
          )}
          {(() => {
            // Locked courier banner — surfaces the customer's chosen courier
            // from the comparison modal so admin/agent/customer all see the
            // exact same "via X · ETA · ₹Y" snapshot the order was billed at.
            const courierName = order.selected_courier_name
              || order.selected_courier?.courier_name
              || order.selected_courier?.name;
            const courierEta = order.selected_courier_eta ?? order.selected_courier?.estimated_days;
            const courierPrice = order.selected_courier_price ?? order.selected_courier?.courier_price;
            const courierProvider = order.selected_courier_provider || order.selected_courier?.courier_id;
            const courierService = order.selected_courier_service;
            if (!courierName) return null;
            return (
              <div
                className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-orange-200 bg-orange-50/70 p-3"
                data-testid="order-details-selected-courier"
              >
                <div className="flex items-center gap-2 text-sm">
                  <Truck className="h-4 w-4 text-[#FF6B00]" />
                  <span className="font-semibold text-slate-900">via {courierName}</span>
                  {courierProvider && (
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold bg-white border border-slate-200 px-1.5 py-0.5 rounded">{courierProvider}</span>
                  )}
                  {courierService && (
                    <span className="text-[10px] text-slate-500">· {courierService}</span>
                  )}
                  {courierEta ? (
                    <span className="text-[10px] text-slate-500">· {courierEta} day{courierEta > 1 ? "s" : ""}</span>
                  ) : null}
                </div>
                {Number.isFinite(courierPrice) && (
                  <span className="text-base font-extrabold text-[#FF6B00]">₹{courierPrice}</span>
                )}
              </div>
            );
          })()}
          {isHub && order.hub_pricing && order.service_type === "delivery" ? (
            <HubPriceCard pricing={order.hub_pricing} isAdmin={isAdmin} />
          ) : (
            <PriceCard price={order.price} />
          )}
          {(() => {
            // Sprint 56 — per-item breakdown table when the order was
            // booked with multiple line items. Shows item name, dims,
            // quantity, slab, and the line total. Hidden for legacy
            // single-item orders.
            const items = order.price?.items_breakdown || [];
            if (!items.length) return null;
            return (
              <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3" data-testid="order-details-items-breakdown">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Parcels in this order</span>
                  <span className="text-[11px] text-slate-400">{items.length} parcels · {(order.price?.items_total_qty || items.length)} units</span>
                </div>
                <div className="space-y-1.5">
                  {items.map((it, i) => (
                    <div
                      key={it.item_id || `item-${i}`}
                      className="flex items-start justify-between rounded-md bg-slate-50/60 px-2.5 py-2 text-xs"
                      data-testid={`items-row-${it.item_id || i}`}
                    >
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{it.item_name || `Parcel ${i + 1}`}</div>
                        <div className="text-[10px] text-slate-500">
                          {it.weight_slab ? `${it.weight_slab}` : `${it.chargeable_weight_kg ?? it.actual_weight_kg ?? "?"} kg`}
                          {(it.length_cm && it.width_cm && it.height_cm) ? ` · ${it.length_cm}×${it.width_cm}×${it.height_cm} cm` : ""}
                          {` · qty ${it.quantity}`}
                        </div>
                      </div>
                      <div className="ml-3 text-right shrink-0">
                        <div className="font-bold text-slate-900">₹{it.line_total}</div>
                        {it.quantity > 1 && (
                          <div className="text-[10px] text-slate-400">₹{it.unit_total} × {it.quantity}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </Section>
      )}

      {/* ── Status timeline ── */}
      {timeline.length > 0 && (
        <Section icon={Clock} title="Status Timeline" testId="order-details-timeline">
          <ol className="space-y-2.5">
            {timeline.map((e, i) => (
              <li
                key={`${e.label}-${i}`}
                className="flex items-start gap-3"
                data-testid={`od-timeline-${i}`}
              >
                <div className={`mt-1 h-2 w-2 rounded-full ${e.danger ? "bg-red-500" : "bg-emerald-500"} flex-shrink-0`} />
                <div>
                  <p className={`text-sm font-semibold ${e.danger ? "text-red-700" : "text-slate-800"}`}>{e.label}</p>
                  <p className="text-xs text-slate-500">{formatDT(e.at)}</p>
                </div>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* ── Assigned agent(s) ── */}
      {(order.agent_name || order.pickup_agent_name || order.delivery_agent_name) && (
        <Section icon={ShieldCheck} title="Assigned Agent" testId="order-details-agent">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {order.agent_name && !isHub && (
              <Field label="Agent" value={order.agent_name} testId="od-agent-name" />
            )}
            {order.agent_phone && !isHub && (isAdmin || isAgent) && (
              <Field label="Agent Phone" value={order.agent_phone} testId="od-agent-phone" />
            )}
            {order.pickup_agent_name && (
              <Field label="First-Mile Agent" value={order.pickup_agent_name} testId="od-pickup-agent-name" />
            )}
            {order.delivery_agent_name && (
              <Field label="Last-Mile Agent" value={order.delivery_agent_name} testId="od-delivery-agent-name" />
            )}
          </div>
        </Section>
      )}

      {/* ── Delivery OTP (customer sees their own; agent/admin don't) ── */}
      {order.delivery_otp && (
        <Section icon={KeyRound} title="Delivery OTP" testId="order-details-otp">
          <p className="text-xs text-slate-500 mb-2">
            Share this 6-digit code with the delivery agent to confirm handover.
          </p>
          <span className="inline-block px-4 py-2 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono text-xl font-bold tracking-widest" data-testid="od-otp-value">
            {order.delivery_otp}
          </span>
        </Section>
      )}
      {isAgent && order.otp_verified && (
        <Section icon={ShieldCheck} title="OTP Verified" testId="order-details-otp-verified">
          <p className="text-sm text-emerald-700 font-medium">
            OTP verified · delivery handoff confirmed.
          </p>
        </Section>
      )}

      {/* ── Sprint 72 — Trust & Transparency: proof photos along the journey ── */}
      {hasJourneyProofs(order) && (
        <Section icon={FileText} title="Proof of journey" testId="order-details-journey-proofs">
          <ProofGallery order={order} size="md" />
        </Section>
      )}

      {/* ── Sprint 74 — Customer NPS prompt on delivered orders (own orders only) ── */}
      {order.status === "delivered" && actorRole === "user" && (
        <Section icon={FileText} title="Rate this delivery" testId="order-details-nps">
          <NPSPrompt trackingId={order.tracking_id} compact />
        </Section>
      )}

      {/* ── Pickup / drop proof photos (if available — schema allows for future upload) ── */}
      {(order.pickup_photo_urls?.length || order.delivery_photo_urls?.length) && (
        <Section icon={FileText} title="Proof Photos" testId="order-details-photos">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {(order.pickup_photo_urls || []).map((u, i) => (
              <a key={`p-${i}`} href={u} target="_blank" rel="noopener noreferrer" className="block">
                <img src={u} alt={`pickup-${i}`} className="rounded-lg object-cover w-full h-24" loading="lazy" />
                <p className="text-[10px] text-slate-400 mt-1">Pickup #{i + 1}</p>
              </a>
            ))}
            {(order.delivery_photo_urls || []).map((u, i) => (
              <a key={`d-${i}`} href={u} target="_blank" rel="noopener noreferrer" className="block">
                <img src={u} alt={`delivery-${i}`} className="rounded-lg object-cover w-full h-24" loading="lazy" />
                <p className="text-[10px] text-slate-400 mt-1">Delivery #{i + 1}</p>
              </a>
            ))}
          </div>
        </Section>
      )}

      {/* ── Admin-only: cancellation + refund ── */}
      {isAdmin && (order.cancellation_reason || order.refund_status || order.admin_notes) && (
        <Section icon={RefreshCcw} title="Admin Notes" testId="order-details-admin" danger={!!order.cancellation_reason}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {order.cancellation_reason && (
              <Field label="Cancellation Reason" value={order.cancellation_reason} testId="od-cancel-reason" />
            )}
            {order.cancelled_at && (
              <Field label="Cancelled At" value={formatDT(order.cancelled_at)} testId="od-cancelled-at" />
            )}
            {order.refund_status && (
              <Field label="Refund" value={order.refund_status} testId="od-refund-status" />
            )}
            {order.refund_amount != null && (
              <Field label="Refund ₹" value={order.refund_amount} testId="od-refund-amount" />
            )}
            {order.admin_notes && (
              <div className="sm:col-span-2">
                <Field label="Admin Notes" value={order.admin_notes} testId="od-admin-notes" />
              </div>
            )}
          </div>
        </Section>
      )}

      {/* ── Rating ── */}
      {order.rating?.score && (
        <Section icon={ShieldCheck} title="Customer Rating" testId="order-details-rating">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-amber-500" data-testid="od-rating-score">
              {"★".repeat(Math.round(order.rating.score))}{"☆".repeat(5 - Math.round(order.rating.score))}
            </span>
            <span className="text-sm text-slate-600">({order.rating.score}/5)</span>
          </div>
          {order.rating.comment && (
            <p className="mt-2 text-sm text-slate-600 italic">"{order.rating.comment}"</p>
          )}
        </Section>
      )}
    </div>
  );
}


function hasJourneyProofs(o) {
  if (!o) return false;
  const isImg = (s) => typeof s === "string"
    && (s.startsWith("data:image/") || s.startsWith("http://") || s.startsWith("https://"));
  return Boolean(
    isImg(o.customer_package_photo) ||
    isImg(o.origin_hub_handover_photo) ||
    isImg(o.packing_photo) ||
    isImg(o.destination_arrival_photo) ||
    (o.proof_events || []).some((e) => isImg(e.image_base64))
  );
}
